import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  FileText,
  Search,
  Loader2,
  Trash2,
  ArrowRight,
  Package,
  Calendar,
  MapPin,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const QuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data || []);
    } catch (error) {
      console.error("Error fetching quotes:", error);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleDelete = async (quoteId) => {
    if (!confirm("Delete this quote?")) return;
    setDeletingId(quoteId);
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success("Quote deleted");
    } catch (error) {
      toast.error("Failed to delete quote");
    } finally {
      setDeletingId(null);
    }
  };

  const handleConvertToWaybill = (quote) => {
    // Navigate to create waybill page with quote data pre-filled
    navigate(`/quotes/request?quote_id=${quote.id}`);
  };

  const filteredQuotes = quotes.filter(q => 
    q.to_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.from_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.reference?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getTotalItems = (contents) => {
    if (!contents?.length) return 0;
    return contents.reduce((sum, c) => sum + (c.pieces || 1), 0);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Saved Quotes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Quotes saved for later - convert to waybill when ready
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchQuotes} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            className="bg-[#16a34a] hover:bg-[#D55415]"
            onClick={() => navigate("/quotes/request")}
          >
            <FileText className="w-4 h-4 mr-2" />
            New Quote
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by company or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Quotes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            {searchTerm ? "No quotes match your search" : "No saved quotes yet"}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Create a quote and save it for later
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQuotes.map((quote) => (
            <div
              key={quote.id}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Route */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-slate-700">{quote.from_company || "Sender"}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="font-semibold text-slate-700">{quote.to_company || "Recipient"}</span>
                  </div>
                  
                  {/* Details */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                    {quote.reference && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Ref: {quote.reference}
                      </span>
                    )}
                    {quote.to_place?.town && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {quote.to_place.town}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {getTotalItems(quote.contents)} item(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(quote.created_at)}
                    </span>
                  </div>

                  {/* Rates if available */}
                  {quote.rates?.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {quote.rates.slice(0, 3).map((rate, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {rate.service}: R{parseFloat(rate.total).toFixed(2)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(quote.id)}
                    disabled={deletingId === quote.id}
                    className="text-red-600 hover:bg-red-50"
                  >
                    {deletingId === quote.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleConvertToWaybill(quote)}
                    className="bg-[#4A7C4E] hover:bg-[#3D6840]"
                  >
                    Create Waybill
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuotesPage;
